import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogIn, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_PARTNER_CODE = 'DEMO';
const DEMO_ICON_URL = 'https://customer-assets.emergentagent.com/job_520f8083-6a7b-456c-a20d-57795878b515/artifacts/0mz8zb1q_DemoLogo.png';

// Blue color scheme matching the demo logo
const DEMO_COLORS = {
  primary: '#1E3A5F',      // Deep blue
  primaryHover: '#152D4A', // Darker blue for hover
  accent: '#3B82F6',       // Bright blue accent
  lightBg: '#EFF6FF',      // Light blue background
  cardBg: '#F0F7FF',       // Card background
};

const DemoLoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [partnerBranding, setPartnerBranding] = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [error, setError] = useState(null);

  // Set up meta tags for home screen icon on mount
  useEffect(() => {
    // Update document title
    document.title = 'Demo APP | Bond Investment Partner';
    
    // Update theme color for blue
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) themeColor.setAttribute('content', DEMO_COLORS.primary);
    
    // Update or create meta tags for PWA
    const updateMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const updateLinkTag = (rel, href, sizes = null) => {
      let selector = `link[rel="${rel}"]`;
      if (sizes) selector += `[sizes="${sizes}"]`;
      let link = document.querySelector(selector);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        if (sizes) link.setAttribute('sizes', sizes);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Update Apple touch icons
    updateLinkTag('apple-touch-icon', DEMO_ICON_URL);
    updateLinkTag('apple-touch-icon', DEMO_ICON_URL, '152x152');
    updateLinkTag('apple-touch-icon', DEMO_ICON_URL, '167x167');
    updateLinkTag('apple-touch-icon', DEMO_ICON_URL, '180x180');
    
    // Update other meta tags
    updateMetaTag('apple-mobile-web-app-title', 'Demo APP');
    updateMetaTag('application-name', 'Demo APP');
    updateMetaTag('msapplication-TileImage', DEMO_ICON_URL);
    updateMetaTag('msapplication-TileColor', DEMO_COLORS.primary);
    
    // Update manifest link
    updateLinkTag('manifest', '/demo-manifest.json');
    
    // Update favicon (will be overridden if partner has custom favicon)
    updateLinkTag('icon', DEMO_ICON_URL);
    updateLinkTag('shortcut icon', DEMO_ICON_URL);

    // Cleanup on unmount - restore original values
    return () => {
      document.title = 'BondMaster | Bond Portfolio Management Platform';
      if (themeColor) themeColor.setAttribute('content', '#1B4D3E');
      updateLinkTag('manifest', '/manifest.json');
      updateLinkTag('icon', 'https://bondmaster.com.in/logoBond.png');
      updateLinkTag('shortcut icon', 'https://bondmaster.com.in/logoBond.png');
      updateMetaTag('apple-mobile-web-app-title', 'BondMaster');
      updateMetaTag('application-name', 'BondMaster');
    };
  }, []);

  // Fetch partner branding
  useEffect(() => {
    const fetchPartnerBranding = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/partner-branding/${DEMO_PARTNER_CODE}`);
        setPartnerBranding(response.data);
        setError(null);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Demo partner not configured. Please contact administrator.');
        } else {
          setError('Failed to load partner information. Please try again.');
        }
      } finally {
        setLoadingBranding(false);
      }
    };

    fetchPartnerBranding();
  }, []);

  // Update favicon and title with partner's custom branding if available
  useEffect(() => {
    if (partnerBranding?.partner_favicon_url) {
      const fav = partnerBranding.partner_favicon_url;
      const name = partnerBranding.partner_name || 'BondMaster';
      document.title = `${name} | Bond Investment Partner`;
      const setLink = (rel, href, sizes) => {
        let sel = `link[rel="${rel}"]`;
        if (sizes) sel += `[sizes="${sizes}"]`;
        let el = document.querySelector(sel);
        if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); if (sizes) el.setAttribute('sizes', sizes); document.head.appendChild(el); }
        el.setAttribute('href', href);
      };
      setLink('icon', fav);
      setLink('shortcut icon', fav);
      setLink('apple-touch-icon', fav);
      setLink('apple-touch-icon', fav, '152x152');
      setLink('apple-touch-icon', fav, '167x167');
      setLink('apple-touch-icon', fav, '180x180');
    }
  }, [partnerBranding]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }
    
    setIsLoading(true);
    try {
      // First, validate credentials with special demo endpoint
      const validateResponse = await axios.post(`${API_URL}/api/auth/demo-login`, {
        username,
        password,
        partner_code: DEMO_PARTNER_CODE
      });
      
      if (validateResponse.data.valid) {
        // Now perform actual login
        const user = await login(username, password);
        toast.success(`Welcome back, ${user.entity_name || user.username}!`);
      } else {
        toast.error('Invalid credentials');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid credentials';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while fetching branding
  if (loadingBranding) {
    return (
      <div className="login-container" data-testid="demo-login-page" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)' }}>
        <div className="w-full max-w-md px-4 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#1E3A5F]/30 border-t-[#1E3A5F] rounded-full mx-auto"></div>
          <p className="text-[#64748b] mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="login-container" data-testid="demo-login-page" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)' }}>
        <div className="w-full max-w-md px-4">
          <Card className="glass-card border-0 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-[#1E3A5F] mb-2">Access Error</h2>
              <p className="text-[#64748b] mb-6">{error}</p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-[#1E3A5F] hover:bg-[#152D4A] text-white"
              >
                Go to Main Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Determine logo
  const hasCustomLogo = partnerBranding?.partner_logo_url && partnerBranding.partner_logo_url.trim() !== '';
  const logoUrl = hasCustomLogo ? partnerBranding.partner_logo_url : 'https://bondmaster.com.in/logoBond.png';

  return (
    <div className="login-container" data-testid="demo-login-page" style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)' }}>
      <div className="w-full max-w-md px-4">
        {/* Logo and Header */}
        <div className="text-center mb-8 animate-fadeIn">
          <img 
            src={logoUrl} 
            alt="Logo" 
            className="h-16 mx-auto mb-4 max-w-[200px] object-contain"
            data-testid="demo-login-logo"
          />
          <p className="text-[#475569] mt-2">
            Your trusted bond investment partner
          </p>
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-xl animate-fadeIn bg-white/80 backdrop-blur-sm" style={{ animationDelay: '0.1s' }}>
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-heading text-center text-[#1E3A5F]">
              Sign In
            </CardTitle>
            <CardDescription className="text-center text-[#64748b]">
              Enter your credentials to access your portfolio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#1E3A5F] font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-11 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]"
                  data-testid="demo-login-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1E3A5F] font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6]"
                  data-testid="demo-login-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 bg-[#1E3A5F] hover:bg-[#152D4A] text-white font-medium rounded-lg transition-all duration-200"
                disabled={isLoading}
                data-testid="demo-login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></span>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-[#64748b] mt-8">
          © 2025. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default DemoLoginPage;
